import React, { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';
import financialService from '../../services/financialService';
import './FinancialDashboard.css';

const PriceExploration = ({ marketplace, cacheKey, dimensionFilter = null, horizonteTemporal = 'Mensual', tipoVolumen = 'Predicciones' }) => {
  const [explorationData, setExplorationData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (expanded && cacheKey && marketplace) {
      loadExploration();
    }
  }, [expanded, cacheKey, dimensionFilter, marketplace, horizonteTemporal, tipoVolumen]);
  
  const loadExploration = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await financialService.getPriceExploration(
        marketplace, 
        cacheKey, 
        dimensionFilter,
        horizonteTemporal,
        tipoVolumen
      );
      
      if (result.success) {
        setExplorationData(result);
      } else {
        setError(result.message || 'No se pudieron cargar los datos de exploración');
      }
    } catch (err) {
      console.error('Error cargando exploración de precios:', err);
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };
  
  if (!cacheKey) {
    return null;
  }
  
  return (
    <div className="price-exploration">
      <button 
        onClick={() => setExpanded(!expanded)}
        className="exploration-toggle"
      >
        🔎 Exploración de precios
        <span className="toggle-icon">{expanded ? '▲' : '▼'}</span>
      </button>
      
      {expanded && (
        <div className="exploration-content">
          {loading && (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Cargando exploración de precios...</p>
            </div>
          )}
          
          {error && (
            <div className="error-container">
              <p>❌ {error}</p>
              <button onClick={loadExploration} className="btn btn-primary">
                🔄 Reintentar
              </button>
            </div>
          )}
          
          {explorationData && !loading && !error && (
            <>
              <div className="exploration-charts">
                <div className="chart-half">
                  <h4>Distribución de precio</h4>
                  {explorationData.histogram_chart && (
                    <Plot
                      data={explorationData.histogram_chart.data}
                      layout={{
                        ...explorationData.histogram_chart.layout,
                        template: 'plotly_white',
                        height: 400,
                        margin: { t: 40, b: 40, l: 60, r: 40 }
                      }}
                      config={{ displayModeBar: false }}
                      style={{ width: '100%' }}
                    />
                  )}
                </div>
                
                <div className="chart-half">
                  <h4>Precio por proveedor</h4>
                  {explorationData.boxplot_chart && explorationData.boxplot_chart.data.length > 0 ? (
                    <Plot
                      data={explorationData.boxplot_chart.data}
                      layout={{
                        ...explorationData.boxplot_chart.layout,
                        template: 'plotly_white',
                        height: 400,
                        margin: { t: 40, b: 40, l: 60, r: 40 }
                      }}
                      config={{ displayModeBar: false }}
                      style={{ width: '100%' }}
                    />
                  ) : (
                    <div className="no-data">
                      <p>No hay suficientes datos para mostrar el boxplot por proveedor</p>
                    </div>
                  )}
                </div>
              </div>
              
              {explorationData.price_changes_table && (
                <div className="price-changes">
                  <h4>Top 20 por incremento de precio aplicado</h4>
                  <div className="price-changes-table">
                    <table>
                      <thead>
                        <tr>
                          {explorationData.price_changes_table.columns.map((col, index) => (
                            <th key={index}>{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {explorationData.price_changes_table.data.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex}>
                                {cellIndex === 4 ? (
                                  <span className={`delta-price ${cell >= 0 ? 'positive' : 'negative'}`}>
                                    {cell >= 0 ? '+' : ''}{cell}
                                  </span>
                                ) : (
                                  cell
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {explorationData.price_comparison && (
                <div className="price-comparison-diagnostics" style={{ marginTop: '20px' }}>
                  <h4>📊 Diagnóstico de Precios por Ventana Temporal</h4>
                  <div className="diagnostics-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginTop: '15px' }}>
                    <div className="diagnostic-card actual-price" style={{ padding: '15px', backgroundColor: '#f0f8ff', borderRadius: '8px', border: '1px solid #b3d9ff' }}>
                      <h5 style={{ margin: '0 0 10px 0', color: '#0066cc' }}>💵 Precio Actual (Publications)</h5>
                      <div className="diagnostic-stats">
                        <div className="stat-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}>
                          <span className="stat-label">Productos:</span>
                          <span className="stat-value"><strong>{explorationData.price_comparison.precio_actual.count.toLocaleString()}</strong></span>
                        </div>
                        <div className="stat-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}>
                          <span className="stat-label">Promedio:</span>
                          <span className="stat-value"><strong>${explorationData.price_comparison.precio_actual.mean.toFixed(2)}</strong></span>
                        </div>
                        <div className="stat-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}>
                          <span className="stat-label">Mediana:</span>
                          <span className="stat-value">${explorationData.price_comparison.precio_actual.median.toFixed(2)}</span>
                        </div>
                        <div className="stat-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: '0.9em' }}>
                          <span className="stat-label">Rango:</span>
                          <span className="stat-value">${explorationData.price_comparison.precio_actual.min.toFixed(2)} - ${explorationData.price_comparison.precio_actual.max.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                    
                    {Object.entries(explorationData.price_comparison.windows || {}).sort((a, b) => a[1].days - b[1].days).map(([key, windowData]) => {
                      const diff = windowData.mean - explorationData.price_comparison.precio_actual.mean;
                      const diffPct = (diff / explorationData.price_comparison.precio_actual.mean) * 100;
                      return (
                        <div key={key} className="diagnostic-card historical-price" style={{ padding: '15px', backgroundColor: '#fff8e1', borderRadius: '8px', border: '1px solid #ffe082' }}>
                          <h5 style={{ margin: '0 0 10px 0', color: '#f57c00' }}>📈 Promedio {windowData.days} días</h5>
                          <div className="diagnostic-stats">
                            <div className="stat-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}>
                              <span className="stat-label">Productos:</span>
                              <span className="stat-value"><strong>{windowData.count.toLocaleString()}</strong></span>
                            </div>
                            <div className="stat-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}>
                              <span className="stat-label">Promedio:</span>
                              <span className="stat-value"><strong>${windowData.mean.toFixed(2)}</strong></span>
                            </div>
                            <div className="stat-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}>
                              <span className="stat-label">Mediana:</span>
                              <span className="stat-value">${windowData.median.toFixed(2)}</span>
                            </div>
                            <div className="stat-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: '0.9em' }}>
                              <span className="stat-label">Rango:</span>
                              <span className="stat-value">${windowData.min.toFixed(2)} - ${windowData.max.toFixed(2)}</span>
                            </div>
                            <div className="stat-row highlight" style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', marginTop: '5px', borderTop: '1px solid #ffe082', fontWeight: 'bold' }}>
                              <span className="stat-label">vs. Actual:</span>
                              <span className="stat-value" style={{ color: diff >= 0 ? '#d32f2f' : '#388e3c' }}>
                                {diff >= 0 ? '+' : ''}{diff.toFixed(2)} ({diffPct >= 0 ? '+' : ''}{diffPct.toFixed(1)}%)
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {(!explorationData.price_comparison.windows || Object.keys(explorationData.price_comparison.windows).length === 0) && (
                    <div className="no-data" style={{ padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px', marginTop: '15px', textAlign: 'center', color: '#666' }}>
                      <p>ℹ️ No hay datos de precios históricos disponibles. Los precios históricos se calculan cuando se selecciona una fuente de precio diferente a "Publications (actual)".</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PriceExploration;
